import { RubyLoader } from '../../../components/RubyLoader';
import { useCallback, useEffect, useRef, useState } from 'react';
import { deviceConfigApi } from '../../../services/deviceConfigApi';
import { api } from '../../../services/apiService';
import { hardwareApi, type DeviceUnitRow, type DeviceModelRow } from '../../../services/hardwareApi';
import { resolvedTargetMapFromEntries, type ResolvedTargetMap, type FleetUnitRecord, type FleetRegisterLinkMap } from '../fleet/fleetRows';
import { canPromote } from './brandingLogic';
import SlotUploadCard from './SlotUploadCard';
import RolloutPanel from './RolloutPanel';
import FleetTable from './FleetTable';
import FleetList from '../../fleet/FleetList';
import DeviceInstallPanel from './DeviceInstallPanel';
import type { DeviceHealthDeviceStat } from '@platform/shared-types';

const DEVICE_LABEL: Record<string, string> = { android_dual_screen: 'Android K10 · dual-screen' };

/**
 * THE FLEET IS EVERY DEVICE WE PUT AN OS ON — not only the ones currently
 * talking to us.
 *
 * Check-ins alone answer "who is online", never "where is the till we shipped
 * on Tuesday". A unit that was installed and has never phoned home is exactly
 * the one worth seeing, so it is merged in as its own row and labelled
 * `installed · never checked in`.
 *
 * The marker is `osInstall`, not `state` and not `appBuild`: it is SERVER-owned
 * and written only by a verified install run (see `recordOsInstall`), so it
 * cannot be typed in by hand, and nothing but the POS install panel ever writes
 * one — which is also why no model-category filter is needed here.
 *
 * ROLLOUT IS PULL-BASED. `resolveTarget` runs inside the check-in handler: a
 * device is handed its target when it asks. So a never-checked-in row can be
 * LISTED but cannot yet have received a release — it collects its build the
 * first time it boots and calls in. The row says so rather than implying cover.
 */
const INSTALLED_STATES = ['configured', 'packing', 'shipped', 'delivered', 'deployed'] as const;

/** Units are paged; one page this size covers a real fleet without a scan-all. */
const UNIT_PAGE = 200;

const toFleetUnit = (u: DeviceUnitRow): FleetUnitRecord => ({
  unitId: u.unitId,
  // NO `deviceId` — deliberately. `unit.terminalId` is a payment-processor TID
  // (a card reader), NOT the `term_…` id a till checks in under, so using it
  // here would join a fleet row to the wrong device. Until a unit records its
  // paired terminal, `buildFleetRows` falls back to matching the unit's
  // tracking id inside the register name, which is the link
  // `CreateRegisterPanel` already enforces when it creates the register.
  deviceId: null,
  trackingId: u.trackingId,
  modelId: u.modelId,
  serialNumber: u.serialNumber || u.trackingId,
  orderId: u.orderId,
  appBuild: u.osInstall?.appBuild || u.appBuild || null,
  installedAt: u.osInstall?.installedAt ?? null,
  installSource: u.osInstall?.source ?? null,
  installTarget: u.osInstall?.target ?? null,
  installVersionCode: u.osInstall?.versionCode ?? null,
  installedBy: u.osInstall?.by ?? null,
  state: u.state ?? null,
  /* Outstanding only. A request older than the install has been carried out,
     and a row that still flagged it would be telling the operator to redo work
     that is already done. */
  reinstallRequestedAt:
    u.reinstall && u.reinstall.requestedAt > (u.osInstall?.installedAt ?? 0)
      ? u.reinstall.requestedAt
      : null,
});

/**
 * Installed units plus the models they point at — the models carry the photos.
 *
 * EXPORTED so the single-device page (`pages/pos/device`) loads its unit the
 * same way the fleet table does. Copying these two gates — the INSTALLED_STATES
 * filter and `!!u.osInstall` — into a second file is how the two views would
 * start disagreeing about whether a device is in the fleet at all.
 */
export const loadInstalledUnits = async (): Promise<{ units: FleetUnitRecord[]; models: DeviceModelRow[] }> => {
  const [pages, models] = await Promise.all([
    Promise.all(
      INSTALLED_STATES.map((state) =>
        hardwareApi.listUnits({ state, limit: UNIT_PAGE }).catch(() => ({ items: [] as DeviceUnitRow[] })),
      ),
    ),
    // A missing catalog costs the photos, never the rows.
    hardwareApi.listModels().catch(() => ({ items: [] as DeviceModelRow[] })),
  ]);
  return {
    units: pages.flatMap((p) => p.items || []).filter((u) => !!u.osInstall).map(toFleetUnit),
    models: models.items || [],
  };
};

/**
 * `layout` only affects the Fleet tab: 'table' (default) is the full FleetTable
 * POS Config has always shown; 'monitoring' is Fleet Monitoring's compact list
 * (same data, same props, no drawer / row actions).
 */
export default function BrandingTab({ tab, health, layout = 'table' }: {
  tab: 'install' | 'branding' | 'fleet';
  health?: Record<string, DeviceHealthDeviceStat>;
  layout?: 'table' | 'monitoring';
}) {
  const [devices, setDevices] = useState<any[]>([]);
  const [cdnDomain, setCdnDomain] = useState<string>('');
  const [statusDevices, setStatusDevices] = useState<any[]>([]);
  /**
   * Why the check-in read failed, or null when it succeeded. A failed `/status`
   * read used to collapse into `devices: []`, and the Fleet table then asserted
   * "No devices have checked in yet" — a factual claim about the fleet made
   * from a read that never returned. Loaded-and-genuinely-empty and
   * failed-to-load are different things and are now rendered differently.
   */
  const [statusError, setStatusError] = useState<string | null>(null);
  /** True when the server said it stopped short of the whole fleet (F1). */
  const [devicesTruncated, setDevicesTruncated] = useState(false);
  const [targetsTruncated, setTargetsTruncated] = useState(false);
  // deviceId → what the SERVER resolved this device should run (Task D6).
  // `null` means the endpoint itself could not be read this cycle — that is a
  // different thing from "resolved nothing for this device", and the Fleet
  // table labels it as such instead of quietly showing every Target as "—".
  const [resolvedTargets, setResolvedTargets] = useState<ResolvedTargetMap | null>({});
  const [deviceType, setDeviceType] = useState<string>('android_dual_screen');
  /** Installed units, merged into the fleet. Only the Fleet tab reads them. */
  const [units, setUnits] = useState<FleetUnitRecord[]>([]);
  const [unitModels, setUnitModels] = useState<DeviceModelRow[]>([]);
  const [loading, setLoading] = useState(true);
  /** registerId → its register. Empty when the lookup failed; never a blocker. */
  const [registerLinks, setRegisterLinks] = useState<FleetRegisterLinkMap>({});
  /**
   * Device ids the lookup DEFINITELY found no register for — a real answer
   * from the server. Kept separate from `registerUnresolved` on purpose: the
   * two must never be merged into one "no register" bucket, or a lookup
   * failure would be rendered as the definite absence the server spent two
   * fix rounds distinguishing it from.
   */
  const [registerMissing, setRegisterMissing] = useState<string[]>([]);
  /**
   * Device ids we simply do NOT know about, because the lookup itself failed
   * (network, 5xx, timeout). "We don't know" — never render as "this device
   * has no register", which is the specific lie the server-side fix removed.
   */
  const [registerUnresolved, setRegisterUnresolved] = useState<string[]>([]);
  const [tenantNames, setTenantNames] = useState<Record<string, string>>({});
  const [orders, setOrders] = useState<Record<string, { orderNo: string; status: string }>>({});
  /**
   * Which enrichment degraded this cycle, in plain language, or null when the
   * last cycle's reads all succeeded. States what is unavailable — never
   * implies anything is wrong with a device — same tone as `devicesError` /
   * `targetsUnavailable` below. Only the most recent failure is kept; each
   * cycle starts clean so a stale message never outlives its cause.
   */
  const [enrichmentError, setEnrichmentError] = useState<string | null>(null);

  /**
   * What the enrichment reads cost when they last ran, kept per source so a
   * cycle that only re-ran ONE of them cannot clear the other's message (or
   * resurrect a message whose cause is gone). Composed into
   * `enrichmentError` after every pass.
   */
  const enrichErrors = useRef<{ links: string | null; tenants: string | null; orders: string | null }>({
    links: null, tenants: null, orders: null,
  });
  /**
   * What the last enrichment pass was FOR, so the 15s poll does not redo work
   * whose inputs have not moved.
   *
   * The register-link lookup fans out one GSI query per device inside a single
   * Lambda; the tenant and order lists are unscoped full reads. Re-running all
   * three every 15 seconds per open tab was roughly 13 Dynamo queries a second
   * per viewer plus two full-table reads a minute — for a device list that
   * changes when a till is installed, i.e. rarely.
   */
  const enrichState = useRef({ idsKey: '', catalogsAt: 0 });
  /** Tenants and orders are re-read this often, not every poll. */
  const CATALOG_TTL_MS = 5 * 60 * 1000;

  /**
   * Second round: everything that can only be asked for once we know WHICH
   * devices are in the fleet. Each read is independently best-effort — a
   * failing tenant list costs the business names and nothing else, exactly as
   * a failing resolved-target read costs only the Target column.
   */
  const enrich = useCallback(async (deviceRows: any[], force = false) => {
    const ids = deviceRows.map((d) => d.deviceId).filter(Boolean);
    // Order-independent: the same fleet in a different order is the same fleet.
    const idsKey = ids.slice().sort().join(',');
    // A failed lookup leaves `idsKey` unchanged, so the next poll retries it —
    // a degraded read is never cached as the answer.
    const doLinks = force || idsKey !== enrichState.current.idsKey;
    const doCatalogs = force || Date.now() - enrichState.current.catalogsAt > CATALOG_TTL_MS;

    if (doLinks) {
      const links = await api.posConfig.getRegisterLinks(ids)
        .then((r) => {
          setRegisterMissing(r.missing || []);
          setRegisterUnresolved(r.unresolved || []);
          // Partial failures (a batch that failed, or one that did not match
          // the shared schema) come back as `unresolved` + `error` rather than
          // a throw, so the devices that DID resolve are still shown.
          enrichErrors.current.links = r.error || null;
          if (!r.error) enrichState.current.idsKey = idsKey;
          return Object.fromEntries(r.links.map((l) => [l.registerId, l]));
        })
        .catch(() => {
          // The read itself failed — every requested id is UNRESOLVED (we know
          // nothing about it), never MISSING (a definite "no register" answer
          // we never actually got).
          setRegisterMissing([]);
          setRegisterUnresolved(ids);
          enrichErrors.current.links = 'Register links unavailable — the lookup could not be read.';
          return {} as FleetRegisterLinkMap;
        });
      setRegisterLinks(links);
    }

    if (doCatalogs) {
      const [tenants, orderRows] = await Promise.all([
        api.tenants.getAll().then((r) => { enrichErrors.current.tenants = null; return r; }).catch(() => {
          enrichErrors.current.tenants = 'Business names unavailable — the tenant list could not be read.';
          return null;
        }),
        hardwareApi.listOrders().then((r) => { enrichErrors.current.orders = null; return r; }).catch(() => {
          enrichErrors.current.orders = 'Order status unavailable — the orders list could not be read.';
          return null;
        }),
      ]);
      // A failed read leaves the PREVIOUS names/orders in place rather than
      // blanking the table on a blip; the banner says the read degraded.
      if (tenants) {
        setTenantNames(Object.fromEntries(
          (tenants as any[])
            // NEVER a raw id as a business name — that is the one thing the
            // card's own rule forbids. No name means no name, and the card
            // renders its "business unavailable" state instead.
            .map((t: any) => [t.id, t.name || t.businessName])
            .filter(([, name]) => !!name),
        ));
      }
      if (orderRows) {
        setOrders(Object.fromEntries(
          ((orderRows as any).items || []).map((o: any) => [o.orderId, { orderNo: o.orderNo, status: o.status }]),
        ));
      }
      // Only a pass where BOTH landed counts as fresh — a failed read must
      // be retried on the next poll, not cached as done for five minutes.
      if (tenants && orderRows) enrichState.current.catalogsAt = Date.now();
    }

    const e = enrichErrors.current;
    setEnrichmentError(e.links || e.tenants || e.orders || null);
  }, []);

  const reload = useCallback((force = false) => {
    Promise.all([
      deviceConfigApi.listDevices(),
      // A failed check-in read is recorded AS a failure — never flattened into
      // an empty device list, which the table would then report as "no devices
      // have checked in yet".
      deviceConfigApi.listDeviceStatus(deviceType).then(
        (s) => ({ ok: true as const, s }),
        (e: any) => ({ ok: false as const, e }),
      ),
      // A missing/failing resolved-target read must never take the fleet table
      // down with it — the branding and check-in columns are independent of it.
      deviceConfigApi.listResolvedTargets(deviceType).catch(() => null),
      // Only the Fleet tab merges units, and a failure here must never take the
      // check-in table down with it — the fleet is then simply the online half.
      tab === 'fleet'
        ? loadInstalledUnits().catch(() => ({ units: [] as FleetUnitRecord[], models: [] as DeviceModelRow[] }))
        : Promise.resolve({ units: [] as FleetUnitRecord[], models: [] as DeviceModelRow[] }),
    ]).then(([r, status, t, u]) => {
      setUnits(u.units);
      setUnitModels(u.models);
      setDevices(r.devices || []);
      setCdnDomain(r.cdnDomain || '');
      const st = status as { ok: boolean; s?: any; e?: any };
      if (st.ok) {
        setStatusDevices(st.s?.devices || []);
        setDevicesTruncated(!!st.s?.truncated);
        setStatusError(null);
      } else {
        setStatusDevices([]);
        setDevicesTruncated(false);
        setStatusError(st.e?.message || 'Could not load device check-ins');
      }
      setResolvedTargets(t ? resolvedTargetMapFromEntries(t.targets, {}, t.deviceType || deviceType) : null);
      setTargetsTruncated(!!(t as any)?.truncated);
      void enrich(st.ok ? (st.s?.devices || []) : [], force);
    }).catch((e: any) => {
      setDevices([]);
      setStatusDevices([]);
      setDevicesTruncated(false);
      setTargetsTruncated(false);
      setStatusError(e?.message || 'Could not load device check-ins');
      setResolvedTargets(null);
    }).finally(() => setLoading(false));
  }, [deviceType, tab, enrich]);

  /** An operator-initiated refresh: re-reads everything, cache or no cache. */
  const forceReload = useCallback(() => { reload(true); }, [reload]);

  useEffect(() => {
    // First pass of a mount is forced; the 15s poll is not, so it re-reads the
    // device list (cheap) without redoing the enrichment fan-out behind it.
    reload(true);
    const timer = setInterval(() => reload(false), 15000);
    return () => clearInterval(timer);
  }, [reload]);

  const device = devices.find((d) => d.deviceType === deviceType);
  const bootCurrent: number | undefined = device?.slots?.boot?.version;
  const bootStaged: number | undefined = device?.slots?.boot?.stagedVersion;
  const promoteGate = canPromote(statusDevices, bootStaged || bootCurrent);

  if (tab === 'install') {
    return <DeviceInstallPanel />;
  }

  if (tab === 'fleet') {
    const FleetView = layout === 'monitoring' ? FleetList : FleetTable;
    return (
      <FleetView
        deviceType={deviceType}
        devices={statusDevices}
        units={units}
        models={unitModels}
        onChanged={forceReload}
        resolvedTargets={resolvedTargets ?? {}}
        targetsUnavailable={resolvedTargets === null}
        targetsTruncated={targetsTruncated}
        devicesError={statusError}
        devicesTruncated={devicesTruncated}
        onRetry={forceReload}
        registerLinks={registerLinks}
        registerMissing={registerMissing}
        registerUnresolved={registerUnresolved}
        tenantNames={tenantNames}
        orders={orders}
        enrichmentError={enrichmentError}
        health={health}
      />
    );
  }

  // tab === 'branding'
  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-600">
        {loading ? <RubyLoader variant="inline" size={14} label="Loading device types…" /> : (
          <span>Device type: <b className="text-gray-900">{DEVICE_LABEL[deviceType] || deviceType}</b>
            {device?.resolution ? <span className="text-gray-400"> · {device.resolution.width}×{device.resolution.height} · boot → <code>{device.bootInstallPath}</code></span> : null}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SlotUploadCard slot="boot" deviceType={deviceType} title="🌀 Device boot"
          subtitle="1st logo on power-on · Android OS boot animation"
          hint="→ real Android bootanimation.zip (≤10s, ≤30MB, loops) · installed to /product/media with root · sha256-verified · can't brick the device"
          slotSummary={device?.slots?.boot || {}} onChanged={forceReload} cdnDomain={cdnDomain} />
        <SlotUploadCard slot="loading" deviceType={deviceType} title="⏳ App loading"
          subtitle="2nd logo · shown while the Ruby Till app starts up"
          hint="image or video · the app shows it at launch (pulled from the manifest — no root)"
          slotSummary={device?.slots?.loading || {}} onChanged={forceReload} cdnDomain={cdnDomain} />
      </div>

      <RolloutPanel deviceType={deviceType} promoteGate={promoteGate}
        bootVersions={{ current: bootCurrent, staged: bootStaged, history: (device?.slots?.boot?.history || []) as number[] }}
        onDone={forceReload} />
    </div>
  );
}
